import React, { useState } from 'react';
import { 
  TextField, 
  Button, 
  Paper, 
  Typography, 
  Box, 
  Link,
  Alert,
  CircularProgress 
} from '@mui/material';
import AuthService from '../services/AuthService';

interface LoginProps {
  onLogin: () => void;
}

const Login: React.FC<LoginProps> = ({ onLogin }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [email, setEmail] = useState('');
  const [resetSent, setResetSent] = useState(false);

  const handleLogin = async (event: React.FormEvent) => {
    event.preventDefault();
    
    if (!username || !password) {
      setError('Bitte geben Sie Benutzername und Passwort ein');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      await AuthService.login(username, password);
      onLogin();
    } catch (err) {
      const errorMessage = err instanceof Error 
        ? err.message 
        : 'Fehler bei der Anmeldung. Bitte versuchen Sie es erneut.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (event: React.FormEvent) => {
    event.preventDefault();
    
    if (!email) {
      setError('Bitte geben Sie Ihre E-Mail-Adresse ein');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await AuthService.requestPasswordReset(email);
      if (response.success) {
        setResetSent(true);
      } else {
        setError(response.error || 'Fehler beim Zurücksetzen des Passworts');
      }
    } catch (err) {
      setError('Fehler beim Zurücksetzen des Passworts');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
      <Paper elevation={3} sx={{ padding: 4, width: '100%', maxWidth: 400 }}>
        <Typography variant="h5" component="h1" align="center" gutterBottom>
          {showForgotPassword ? 'Passwort zurücksetzen' : 'Anmelden'}
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {resetSent && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Wenn ein Konto mit dieser E-Mail existiert, haben wir einen Link zum Zurücksetzen des Passworts gesendet.
          </Alert>
        )}

        {!showForgotPassword ? (
          <Box component="form" onSubmit={handleLogin} noValidate>
            <TextField
              margin="normal"
              required
              fullWidth
              id="username"
              label="Benutzername"
              name="username"
              autoComplete="username"
              autoFocus
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              disabled={loading}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Passwort"
              type="password"
              id="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={loading}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              color="primary"
              disabled={loading}
              sx={{ mt: 3, mb: 2 }}
            >
              {loading ? <CircularProgress size={24} /> : 'Anmelden'}
            </Button>
            <Box textAlign="center">
              <Link 
                href="#" 
                onClick={(e) => {
                  e.preventDefault();
                  setShowForgotPassword(true);
                  setError(null);
                }}
                sx={{ cursor: 'pointer' }}
              >
                Passwort vergessen?
              </Link>
            </Box>
          </Box>
        ) : (
          <Box component="form" onSubmit={handleForgotPassword} noValidate>
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="E-Mail-Adresse"
              name="email"
              autoComplete="email"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={loading || resetSent}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              color="primary"
              disabled={loading || resetSent}
              sx={{ mt: 3, mb: 2 }}
            >
              {loading ? <CircularProgress size={24} /> : 'Passwort zurücksetzen'}
            </Button>
            <Box textAlign="center">
              <Link 
                href="#" 
                onClick={(e) => {
                  e.preventDefault();
                  setShowForgotPassword(false);
                  setError(null);
                  setResetSent(false);
                }}
                sx={{ cursor: 'pointer' }}
              >
                Zurück zur Anmeldung
              </Link>
            </Box>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default Login;